import React from 'react';
import {
  BG_CARD,
  BORDER_LIGHT,
  TEXT_DARK,
  TEXT_MUTED,
} from '../components/legacy-ball/table-management/designTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AgendaItem {
  id: string;
  title: string;
  speaker: string | null;
  startTime: string;
  endTime: string;
  sortOrder: number;
}

export interface AgendaPanelProps {
  performers: AgendaItem[];
}

// ─── Formatting Helper ───────────────────────────────────────────────────────

/**
 * Extracts HH:MM from an ISO string or time string and converts to 12-hour AM/PM.
 * Does NOT use `new Date()` to avoid timezone conversion issues.
 */
export function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number;
  let minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    overflowY: 'auto',
    maxHeight: '400px',
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
  emptyState: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    textAlign: 'center' as const,
    padding: '2rem 1rem',
  },
  item: {
    padding: '0.75rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  itemLast: {
    padding: '0.75rem 0',
    borderBottom: 'none',
  },
  timeRange: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginBottom: '0.25rem',
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  speaker: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginTop: '0.15rem',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function AgendaPanel({ performers }: AgendaPanelProps) {
  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Agenda</h3>
      {performers.length === 0 ? (
        <div style={styles.emptyState}>No agenda items are scheduled</div>
      ) : (
        performers.map((item, index) => (
          <div
            key={item.id}
            style={index === performers.length - 1 ? styles.itemLast : styles.item}
          >
            <div style={styles.timeRange}>
              {formatTime(item.startTime)} – {formatTime(item.endTime)}
            </div>
            <div style={styles.title}>{item.title}</div>
            {item.speaker && (
              <div style={styles.speaker}>🎤 {item.speaker}</div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
